import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import type { Film, Showtime } from '@vista-digital/ocapi-types/v1'
import { getFilmById, getShowtimeById, loadFilmById, loadShowtimeById } from '@vista-digital/occ-domain'
import type { OccI18N } from '@vista-digital/occ-ui'
import type { DependencyList } from 'react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom-v7'
import { gtm } from '../gtm.service'
import { getUserPath, UserPath } from '../utils/getUserPath'
import { useGtmEvent } from '.'

const logger = getLogger('GTM')

export function useGtmViewShowtimeFilm(
    showtimeId?: string | null,
    conditions?: DependencyList,
    onComplete?: () => void
) {
    // if (!showtimeId) return
    logger.value.trace('useGtmViewShowtimeFilm', { showtimeId, conditions })

    const [showtime, setShowtime] = useState<Showtime | undefined>(showtimeId ? getShowtimeById(showtimeId) : undefined)
    const [searchParams, setSearchParams] = useSearchParams()

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recalculate the user path when the showtime changes
    const showtimeUserPath = useMemo(() => getUserPath(searchParams, setSearchParams), [showtimeId])

    if (!showtime && showtimeId) {
        void loadShowtimeById(showtimeId).then(() => {
            setShowtime(getShowtimeById(showtimeId))
        })
    }

    const filmId = showtime?.filmId

    const isQuickBookOrGoogle = showtimeUserPath === UserPath.Google || showtimeUserPath === UserPath.QuickBook

    useGtmFilmEvent(
        (i18n, film) => {
            // Use user path fetched in this hook, userPath from useGtmFilmEvent is fetched after quickBook url param is cleared.
            gtm.ecommerce.films.viewItem(film, showtimeUserPath, i18n)
            onComplete?.()
        },
        filmId,
        [filmId, isQuickBookOrGoogle, showtimeUserPath, ...(conditions || [])]
    )
}

export function useGtmViewFilm(filmId: string, conditions?: DependencyList) {
    logger.value.trace('gtmGtmViewFilm', { filmId })

    useGtmFilmEvent(
        (i18n, film, userPath) => {
            gtm.ecommerce.films.viewItem(film, userPath, i18n)
        },
        filmId,
        [filmId, ...(conditions || [])]
    )
}

export function useGtmFilmEvent(
    event: (i18n: OccI18N, film: Film, userPath: string) => void,
    filmId: string | undefined,
    conditions?: DependencyList
) {
    logger.value.trace('useGtmFilmEvent', { filmId })

    const [searchParams, setSearchParams] = useSearchParams()
    const [film, setFilm] = useState<Film | undefined>(filmId ? getFilmById(filmId) : undefined)

    if (film?.id !== filmId && filmId) {
        loadFilmById(filmId)
            .then(() => {
                setFilm(getFilmById(filmId))
            })
            .catch(() => {})
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- only recalculate the user path when the film changes
    const userPath = useMemo(() => getUserPath(searchParams, setSearchParams), [filmId])

    useGtmEvent(
        i18n => {
            event(i18n, film!, userPath)
        },
        [film, film?.id === filmId, userPath !== undefined, ...(conditions || [])],
        [film?.id || filmId]
    )
}
